/**
 * PASTED CREDENTIALS — mask a credential a PERSON typed into our own data, before we echo it.
 *
 * BUG-muzhg0nt-n81q, slice 1. This repo already has two redactors, `redactWebsiteToken` and
 * `redactVercelToken`, and both run the same direction: take a secret the PIPELINE HOLDS (from env)
 * out of text it is about to print. Neither covers the opposite direction — a secret a HUMAN put
 * into bug data, which the CLI then prints into a retained CI log. The bug-pipeline board's
 * guidance box is exactly where that happens: it is where you answer a tick that said "I need
 * access to X", so it is the one field in the system most likely to receive a key. One did, on
 * 2026-10-08, and `queue.mts list` prints that field on every row.
 *
 * So this module is a LEAF with no imports — the CLI loads it before it prints anything, the write
 * boundary (slice 2) and the scrub (slice 3) will load the same one, and nothing it does can fail
 * for a reason outside itself.
 *
 * TWO DESIGN RULES, both load-bearing:
 *
 * 1. **It REPLACES, it does not delete.** The span becomes a visible marker naming what was there
 *    and how long it was — «credential redacted · RSA private key · 1,704 chars». A tick does need
 *    to know you supplied a credential (that is usually the answer to its own question); it has
 *    never needed the bytes. Silent deletion would make an answered question look unanswered.
 *
 * 2. **It fires on SHAPE, and the negative traps are half the specification.** Over-redaction here
 *    eats a legitimate instruction mid-sentence, which is worse than useless because the reader
 *    cannot tell it happened. So every rule carries an opacity guard, and the test battery pins the
 *    things that must NOT be touched: a base64 image data URL, a git sha, a `dpl_` deployment id, a
 *    `BUG-` id, a `prod_` product id, and the lowercase-slug ids this codebase is full of.
 */

/** What a finding IS — the vocabulary, closed so a marker can never say something unexplained. */
export type PastedCredentialKind =
  | 'pem-private-key'
  | 'json-secret-value'
  | 'google-api-key'
  | 'github-token'
  | 'openai-key'
  | 'slack-token'
  | 'aws-access-key-id'
  | 'labelled-secret'

export interface PastedCredentialFinding {
  kind: PastedCredentialKind
  /** Human words for the marker — "RSA private key", not "pem-private-key". */
  label: string
  /** Half-open span into the ORIGINAL string. */
  start: number
  end: number
  /** Length of the masked span, which is what the marker reports. */
  chars: number
}

/**
 * The fields a PERSON writes on a bug report. The detector is shape-based and runs over every
 * string the print door is handed, so this list is not a filter — it is documentation of where a
 * pasted credential actually lands, and slice 2's write boundary reads it to decide what to check
 * before saving.
 */
export const HUMAN_WRITTEN_BUG_FIELDS = [
  'pipelineOperatorGuidance',
  'description',
  'expectedBehavior',
  'stepsToReproduce',
  'workaroundDetails',
  'approvalNote',
] as const

/** A thousands separator without reaching for Intl — this module imports nothing. */
function withCommas(n: number): string {
  const s = String(n)
  let out = ''
  for (let i = 0; i < s.length; i += 1) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ','
    out += s[i]
  }
  return out
}

/** The marker that takes the credential's place. Visible on purpose. */
export function pastedCredentialMarker(finding: Pick<PastedCredentialFinding, 'label' | 'chars'>): string {
  return `«credential redacted · ${finding.label} · ${withCommas(finding.chars)} chars»`
}

/** Anything already masked must never be re-detected, or markers nest on a second pass. */
const MARKER_OPEN = '«credential redacted'

// ── The rules, in precedence order (lower priority number wins an overlap) ───────────────────────

const PEM_BEGIN = /-----BEGIN ([A-Z0-9 ]*?)PRIVATE KEY( BLOCK)?-----/g

/** "RSA" + " BLOCK" → "RSA private key block"; "" → "private key". */
function pemLabel(family: string, block: string | undefined): string {
  const fam = family.trim()
  return `${fam ? `${fam} ` : ''}private key${block ? ' block' : ''}`
}

/**
 * The JSON shapes a service-account / config paste arrives in. The VALUE is masked, the key name is
 * kept — "which secret did they send" is exactly what the tick needs and never the secret itself.
 */
const JSON_SECRET_KEYS = [
  'private_key', 'privateKey', 'client_secret', 'clientSecret', 'refresh_token', 'refreshToken',
  'access_token', 'accessToken', 'api_key', 'apiKey', 'password', 'secret', 'token',
]

/** Label words that make the run after them a credential rather than an id. */
const LABEL_WORDS = [
  'secret', 'secret[_-]?key', 'token', 'password', 'passwd', 'pwd', 'api[_-]?key', 'apikey',
  'access[_-]?key', 'private[_-]?key', 'credentials?', 'bearer', 'auth[_-]?token', 'app[_-]?password',
]

/**
 * `(?<![A-Za-z])` and not `\b`: the label usually arrives INSIDE a screaming-snake env name —
 * `MEASUREMENT_API_KEY=…`, `WFR_CLIENT_SECRET: …` — where `_API` has no word boundary at all, so a
 * leading `\b` matches none of the real cases. A digit or an underscore before the word is fine; a
 * letter is not (`tokenizer:` must not read as `token`).
 */
const LABELLED_RUN = new RegExp(
  `(?<![A-Za-z])(${LABEL_WORDS.join('|')})\\b["']?\\s*(?:=|:|=>|->|\\bis\\b)\\s*["'\`]?([A-Za-z0-9+/=_.-]{24,})`,
  'gi',
)

/** A git sha, a deployment id, a bug id, a product id, a lowercase slug — all ids, none secrets. */
function looksLikeAnId(run: string): boolean {
  if (/^(?:dpl_|BUG-|q_BUG-|prod_|rec_|sku_|batch[_-])/i.test(run)) return true
  if (/^(?:https?:|data:)/i.test(run)) return true
  // git shas: the exact lengths git prints. A 64-hex value is plausibly a real secret, so only
  // these three are excused.
  if (/^[0-9a-f]+$/.test(run) && [7, 8, 40].includes(run.length)) return true
  // A lowercase-and-separators slug is an id in this codebase, not an opaque secret
  // (prod ids, SKU names, recipe keys, `goodiez-gummies-strawberry-lemonade`).
  if (/^[a-z][a-z0-9]*(?:[-_.][a-z0-9]+)+$/.test(run)) return true
  return false
}

/**
 * Does a run after a credential LABEL actually look opaque? A digit plus either a capital or real
 * length is what separates `API_KEY=AIzaSyB3xK9…` from `password is required for this screen`.
 */
function looksOpaque(run: string): boolean {
  if (run.length < 24) return false
  if (run.includes(MARKER_OPEN)) return false
  if (looksLikeAnId(run)) return false
  if (!/[0-9]/.test(run)) return false
  if (!/[A-Z]/.test(run) && run.length < 32) return false
  return true
}

function pushAll(re: RegExp, text: string, make: (m: RegExpExecArray) => PastedCredentialFinding | null,
  into: Array<{ f: PastedCredentialFinding; priority: number }>, priority: number): void {
  re.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const f = make(m)
    if (f && f.end > f.start) into.push({ f, priority })
    if (m.index === re.lastIndex) re.lastIndex += 1
  }
}

/**
 * Every credential shape in `text`, as non-overlapping spans. Overlaps resolve by PRECEDENCE, not
 * by position: a service-account JSON matches both the `"private_key"` value rule and the PEM rule
 * over nearly the same span, and "RSA private key" is the more useful marker than "JSON token
 * value", so PEM wins.
 */
export function findPastedCredentials(text: unknown): PastedCredentialFinding[] {
  if (typeof text !== 'string' || text.length === 0) return []
  const candidates: Array<{ f: PastedCredentialFinding; priority: number }> = []

  // 1. PEM private keys. Terminated by preference; an unterminated BEGIN still gets its body eaten,
  //    because a key pasted without its footer is every bit as usable.
  pushAll(PEM_BEGIN, text, (m) => {
    const label = pemLabel(m[1] ?? '', m[2])
    const after = text.slice(m.index + m[0].length)
    const end = after.match(/^[\s\S]*?-----END [A-Z0-9 ]*?PRIVATE KEY(?: BLOCK)?-----/)
    if (end) {
      const chars = m[0].length + end[0].length
      return { kind: 'pem-private-key', label, start: m.index, end: m.index + chars, chars }
    }
    // No footer: take the base64 body that follows (literal newlines or the escaped \n a JSON
    // paste carries), and nothing else.
    const body = after.match(/^(?:[A-Za-z0-9+/=]|\s|\\n|\\r)*/)
    const chars = m[0].length + (body ? body[0].length : 0)
    return { kind: 'pem-private-key', label, start: m.index, end: m.index + chars, chars }
  }, candidates, 1)

  // 2. Vendor token shapes. Fixed prefixes, so no opacity guard is needed — except `sk-`, which
  //    would otherwise swallow any long lowercase slug beginning "sk-".
  pushAll(/\bAIza[0-9A-Za-z_-]{35}/g, text, (m) => ({
    kind: 'google-api-key', label: 'Google API key', start: m.index, end: m.index + m[0].length, chars: m[0].length,
  }), candidates, 2)
  pushAll(/\bgh[pousr]_[A-Za-z0-9]{36,255}/g, text, (m) => ({
    kind: 'github-token', label: 'GitHub token', start: m.index, end: m.index + m[0].length, chars: m[0].length,
  }), candidates, 2)
  pushAll(/\bgithub_pat_[A-Za-z0-9_]{22,}/g, text, (m) => ({
    kind: 'github-token', label: 'GitHub token', start: m.index, end: m.index + m[0].length, chars: m[0].length,
  }), candidates, 2)
  pushAll(/\bsk-(?:proj-|ant-)?[A-Za-z0-9_-]{20,}/g, text, (m) => (
    /[0-9]/.test(m[0]) && /[A-Z]/.test(m[0])
      ? { kind: 'openai-key', label: 'API secret key', start: m.index, end: m.index + m[0].length, chars: m[0].length }
      : null
  ), candidates, 2)
  pushAll(/\bxox[abeprs]-[A-Za-z0-9-]{10,}/g, text, (m) => ({
    kind: 'slack-token', label: 'Slack token', start: m.index, end: m.index + m[0].length, chars: m[0].length,
  }), candidates, 2)
  pushAll(/\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g, text, (m) => ({
    kind: 'aws-access-key-id', label: 'AWS access key id', start: m.index, end: m.index + m[0].length, chars: m[0].length,
  }), candidates, 2)

  // 3. A named secret in a JSON blob — mask the VALUE, keep the key name.
  for (const key of JSON_SECRET_KEYS) {
    const re = new RegExp(`"${key}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 'g')
    pushAll(re, text, (m) => {
      const value = m[1] ?? ''
      if (value.length < 16 || value.includes(MARKER_OPEN)) return null
      const valueStart = m.index + m[0].length - 1 - value.length
      return {
        kind: 'json-secret-value', label: `JSON "${key}" value`,
        start: valueStart, end: valueStart + value.length, chars: value.length,
      }
    }, candidates, 3)
  }

  // 4. A long opaque run right after a credential label.
  pushAll(LABELLED_RUN, text, (m) => {
    const run = m[2] ?? ''
    if (!looksOpaque(run)) return null
    const start = m.index + m[0].length - run.length
    return { kind: 'labelled-secret', label: `${(m[1] ?? 'secret').toLowerCase()} value`, start, end: start + run.length, chars: run.length }
  }, candidates, 4)

  // Precedence first, then position; keep a candidate only if it overlaps nothing already kept.
  candidates.sort((a, b) => a.priority - b.priority || a.f.start - b.f.start || b.f.chars - a.f.chars)
  const kept: PastedCredentialFinding[] = []
  for (const c of candidates) {
    if (kept.some(k => c.f.start < k.end && k.start < c.f.end)) continue
    kept.push(c.f)
  }
  return kept.sort((a, b) => a.start - b.start)
}

/** `text` with every credential shape replaced by its marker. Idempotent. */
export function redactPastedCredentials(text: unknown): string {
  if (typeof text !== 'string') return String(text ?? '')
  const findings = findPastedCredentials(text)
  if (findings.length === 0) return text
  let out = ''
  let cursor = 0
  for (const f of findings) {
    out += text.slice(cursor, f.start) + pastedCredentialMarker(f)
    cursor = f.end
  }
  return out + text.slice(cursor)
}

/** True when `text` carries at least one credential shape — the write boundary's question. */
export function hasPastedCredential(text: unknown): boolean {
  return findPastedCredentials(text).length > 0
}

/**
 * One line for a warning or a log, naming the kinds and sizes and never the bytes.
 * "2 pasted credentials redacted: RSA private key (1,704 chars), Google API key (39 chars)".
 */
export function describePastedCredentials(findings: PastedCredentialFinding[]): string {
  if (findings.length === 0) return ''
  const each = findings.map(f => `${f.label} (${withCommas(f.chars)} chars)`).join(', ')
  return `${findings.length} pasted credential${findings.length === 1 ? '' : 's'} redacted: ${each}`
}

/**
 * Walk any JSON-shaped value and redact every string in it — the form the CLI's print door uses.
 *
 * Deliberately NOT keyed on field names. The spec named the four fields a person writes, and a
 * name-keyed allowlist is exactly the guard that leaks the NEXT field someone pastes into: a new
 * command, a renamed field, or `resolutionSummary` instead of `description`, and the credential is
 * back in the log. The detector fires on shape, so running it over everything costs nothing and
 * closes the door rather than six keyholes. `found` reports what it did for the caller to log.
 */
export function redactPastedCredentialsDeep<T>(value: T): { value: T; found: PastedCredentialFinding[] } {
  const found: PastedCredentialFinding[] = []
  const walk = (node: unknown): unknown => {
    if (typeof node === 'string') {
      const findings = findPastedCredentials(node)
      if (findings.length === 0) return node
      found.push(...findings)
      return redactPastedCredentials(node)
    }
    if (Array.isArray(node)) return node.map(walk)
    if (node && typeof node === 'object' && (node.constructor === Object || node.constructor === undefined)) {
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) next[k] = walk(v)
      return next
    }
    return node
  }
  return { value: walk(value) as T, found }
}

// ── SLICE 2: THE WRITE BOUNDARY ──────────────────────────────────────────────────────────────────
//
// Slice 1 stopped the CLI ECHOING a pasted credential. It un-stored nothing, and it does not stop
// the next paste from being stored: a print-time redactor runs after the bytes are already in our
// database and in every log written before it shipped. This half is the one that prevents the
// exposure — it refuses the write.
//
// It lives HERE, in the leaf, for the reason the print door does: the board, the two bug pages, the
// bug form and three API routes all have to agree on what counts as a credential and on what the
// person is told. Six copies of that sentence is six chances for one of them to drift into "warn"
// while the operator believes the system refuses.

/**
 * The keys a write boundary screens — the fields a PERSON types PROSE into, under both the names
 * they are STORED as and the semantic names the routes accept from a client.
 *
 * Name-keyed on purpose, and that is the opposite of the print door's rule, which runs over
 * everything. The asymmetry is deliberate: printing a string costs nothing, so the door closes
 * rather than six keyholes. REFUSING a write is a thing a person collides with, and the payloads
 * here also carry attachment bytes and data URLs — the two places a shape detector is most likely
 * to be wrong, and the most expensive place to be wrong, because the person loses their typing.
 */
export const PASTED_CREDENTIAL_WRITE_KEYS = [
  ...HUMAN_WRITTEN_BUG_FIELDS,
  // The semantic keys the routes take from a client and map to a stored field.
  'pipelineGuidance',  // the board's per-bug answer box → pipelineOperatorGuidance
  'queueGuidance',     // the plan card's box, which fans out onto EVERY member of the plan
  'answer',            // the reporter answering the pipeline's open question, on their own bug page
  'resolutionSummary',
  'title',
] as const

/**
 * The semantic key a client sends to proceed anyway.
 *
 * The override is in the specification and it is not a loophole: the operator's stated default is
 * "refuse, with a paste-anyway override under the sentence explaining where it will end up — a
 * speed bump for the honest case and a wall for the accidental one". Somebody will one day have a
 * real reason, and a wall with no door gets worked around in a way nobody can see. The server logs
 * every use by KIND AND LENGTH, never by value.
 */
export const PASTED_CREDENTIAL_OVERRIDE_KEY = 'pasteCredentialAnyway'

export interface PastedCredentialScreen {
  /** True when nothing credential-shaped was found, or the caller overrode. */
  ok: boolean
  /** Every finding, across every screened field. Empty when ok and not overridden. */
  findings: PastedCredentialFinding[]
  /** Which screened keys carried one — what the message names. */
  fields: string[]
  /** The sentence the person reads. Empty when nothing was found. */
  message: string
  /** True when findings existed and the caller asked to proceed regardless. */
  overridden: boolean
}

/**
 * What the person reads. It has to carry three things, and the middle one is the whole point:
 * WHAT was found, WHERE the text ends up, and what to send INSTEAD. "Looks like a secret" on its
 * own reads as prudishness; "this will be printed into CI logs we keep" is a fact that changes the
 * decision.
 */
export function pastedCredentialRefusal(findings: readonly PastedCredentialFinding[], fields: readonly string[] = []): string {
  if (findings.length === 0) return ''
  const what = findings.map(f => `${f.label} (${withCommas(f.chars)} chars)`).join(', ')
  const where = fields.length ? ` in ${fields.join(', ')}` : ''
  return `That looks like a credential${where}: ${what}. `
    + 'Bug text is stored in our database and printed into CI logs that are kept, so pasting a key '
    + 'here exposes it to everyone with repo access and still does not deliver it to the pipeline. '
    + 'Put it in Vercel env and send the VARIABLE NAME instead — that is all a tick needs. '
    + 'If you really mean to store it here, use “paste anyway”.'
}

/**
 * THE WALL. Screen a write payload before it is saved.
 *
 * Reads only `PASTED_CREDENTIAL_WRITE_KEYS`, and only their string values. A payload with no
 * screened key is `ok` — this never becomes a reason an unrelated write fails.
 */
export function screenPastedCredentialWrite(payload: unknown): PastedCredentialScreen {
  const empty: PastedCredentialScreen = { ok: true, findings: [], fields: [], message: '', overridden: false }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return empty

  const body = payload as Record<string, unknown>
  const findings: PastedCredentialFinding[] = []
  const fields: string[] = []
  for (const key of PASTED_CREDENTIAL_WRITE_KEYS) {
    const value = body[key]
    if (typeof value !== 'string' || !value) continue
    const found = findPastedCredentials(value)
    if (found.length === 0) continue
    findings.push(...found)
    fields.push(key)
  }
  if (findings.length === 0) return empty

  const overridden = body[PASTED_CREDENTIAL_OVERRIDE_KEY] === true
  return {
    ok: overridden,
    findings,
    fields,
    message: pastedCredentialRefusal(findings, fields),
    overridden,
  }
}

/**
 * The override key off the payload, so it is never PERSISTED.
 *
 * `createBugReport` and the per-bug PATCH both SPREAD the request body onto the stored row, so a
 * transport-only key left on it becomes a permanent column on that bug. Same reason
 * `screenshotUrls` is destructured away in `createBugReport`.
 */
export function stripPastedCredentialOverride<T extends object>(payload: T): T {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload
  if (!(PASTED_CREDENTIAL_OVERRIDE_KEY in payload)) return payload
  const { [PASTED_CREDENTIAL_OVERRIDE_KEY]: _transportOnly, ...rest } = payload as Record<string, unknown>
  return rest as T
}

/**
 * The refusal as DATA — status and body — so every route that screens a write sends the identical
 * answer and a client can key off one `code`.
 *
 * 422 and not 400: the payload is well-formed JSON and the refusal is about its CONTENT. The
 * findings are reduced to kind, label and length; the span and the value never leave the server,
 * because an error message is itself a place a credential gets echoed.
 */
export function pastedCredentialRefusalPayload(screen: PastedCredentialScreen): {
  status: number
  body: {
    error: string
    code: 'PASTED_CREDENTIAL'
    fields: string[]
    found: { kind: PastedCredentialKind; label: string; chars: number }[]
  }
} {
  return {
    status: 422,
    body: {
      error: screen.message,
      code: 'PASTED_CREDENTIAL',
      fields: screen.fields,
      found: screen.findings.map(f => ({ kind: f.kind, label: f.label, chars: f.chars })),
    },
  }
}

// ── SLICE 3 — THE SCRUB: take a credential OUT of what is already stored ────────────────────────
//
// Slice 1 stopped the CLI echoing a pasted credential; slice 2 refuses the next write. Neither
// reaches the bytes already sitting in the bug database — a print-time redactor runs after the
// store, and the write boundary only ever sees a NEW write. So the key that caused this item is
// still on BUG-muwdjbwc-on97, and rotating it in Google would leave our copy where it is.
//
// This is the remediation half: replace the stored span with the SAME marker, in place, so
// rotating a key and clearing our copy of it are one job instead of two.
//
// TWO RULES, and the second is what makes it safe to run at all:
//
// 1. **It writes only credential bytes.** The patch is COMPUTED from the stored text — never
//    supplied — and `scrubKeptEverythingElse` proves, per field, that every character outside the
//    credential spans is byte-identical. So this command cannot become a general bug-text editor,
//    which is the thing a tick must not have. (A scrub that could rewrite prose would be able to
//    erase the operator's own answer to a question and leave a plausible-looking record.)
//
// 2. **It is idempotent and it says so.** A second run finds nothing, writes nothing, and reports
//    `clean` — the markers are not re-detected (`findPastedCredentials` skips them), so a re-run is
//    a no-op rather than a nesting marker.
//
// DETECTION IS TOTAL, THE WRITE IS SCOPED, AND THE GAP IS NAMED. The plan looks at every string on
// the row; it rewrites only `PASTED_CREDENTIAL_SCRUB_FIELDS`. Anything found elsewhere comes back
// as `unwritable` and gets printed — a finding the scrub silently skipped would read as a clean bug.

/**
 * The STORED fields a scrub may rewrite: the prose a person types, plus the prose a tick writes
 * about it (a recommendation or a work-log entry can quote the guidance box it is answering).
 *
 * Every one of these is in `BugReportUpdate`, the guarded writer's whitelist — pinned by test,
 * because a field outside it would be planned, reported as written, and silently dropped.
 *
 * `title` is deliberately NOT here even though the detector reads it. The title is the bug's NAME:
 * it is already in every email sent about the row, in its queue plan and in the work log, so
 * rewriting it makes the record unrecognisable without removing the credential from anywhere it
 * has actually travelled. A finding there is reported as `unwritable` and is a person's call.
 */
export const PASTED_CREDENTIAL_SCRUB_FIELDS = [
  ...HUMAN_WRITTEN_BUG_FIELDS,
  'resolutionSummary',
  'fixRecommendation',
  'pipelineFixNotes',
  'pipelineWorkLog',
  'pipelineBlockedReason',
] as const

/**
 * The prose fields on an INTAKE QUEUE card (`lib/bug-queues.ts` IntakeQueue).
 *
 * The queue card is the OTHER place the same paste lands, and it is not a detail. The board's
 * queue-card answer box writes the operator's text onto the queue AND copies it onto every member
 * (`setQueueGuidance` — "the answer is written in two places on purpose"), so one paste into a
 * three-member plan is FOUR stored copies. Measured on the real incident, 2026-10-08: the key was
 * on q_BUG-muwdixpq-3qcs and on all three of its members. A scrub that cleaned one bug would have
 * reported success with three copies left behind.
 */
export const PASTED_CREDENTIAL_QUEUE_SCRUB_FIELDS = ['guidance', 'plan', 'openQuestions'] as const

export interface PastedCredentialScrubField {
  field: string
  findings: PastedCredentialFinding[]
  /** The stored text with every credential span replaced by its marker. */
  after: string
  /** How many credential characters leave the database. */
  chars: number
}

export interface PastedCredentialScrubPlan {
  /** True when nothing credential-shaped is stored — nothing to do, and a re-run says this. */
  clean: boolean
  /** The scrubbable fields that change, in `PASTED_CREDENTIAL_SCRUB_FIELDS` order. */
  fields: PastedCredentialScrubField[]
  /** field → redacted text. The whole write, and nothing else. */
  patch: Record<string, string>
  findings: PastedCredentialFinding[]
  /** Findings in a field this command does not rewrite — NAMED, never silently skipped. */
  unwritable: { field: string; findings: PastedCredentialFinding[] }[]
  /** One line, by kind and length, never by value. */
  report: string
  /** Non-null means the plan is REFUSED — the only caller must not write. */
  refuse: string | null
}

/**
 * Did the redaction keep every non-credential character?
 *
 * Strip the findings' spans out of the original and the markers out of the result: what is left
 * must be byte-identical. This is the invariant that makes the scrub safe to run — without it
 * "replace a credential span" and "rewrite this field" are the same API.
 */
export function scrubKeptEverythingElse(
  before: string, after: string, findings: readonly PastedCredentialFinding[],
): boolean {
  let kept = ''
  let cursor = 0
  for (const f of findings) {
    kept += before.slice(cursor, f.start)
    cursor = f.end
  }
  kept += before.slice(cursor)
  const afterKept = after.split(/«credential redacted · [^»]*»/).join('')
  return kept === afterKept
}

/** One line for the operator: which fields, which kinds, how many characters leave the database. */
export function describePastedCredentialScrub(plan: PastedCredentialScrubPlan): string {
  if (plan.clean) return 'nothing credential-shaped is stored on this bug'
  const each = plan.fields.map(f => `${f.field}: ${f.findings.map(g => `${g.label} (${withCommas(g.chars)} chars)`).join(', ')}`)
  const extra = plan.unwritable.map(u => `${u.field} — NOT rewritten, a person's call: `
    + u.findings.map(g => `${g.label} (${withCommas(g.chars)} chars)`).join(', '))
  const chars = plan.fields.reduce((n, f) => n + f.chars, 0)
  const lead = plan.fields.length === 0
    ? 'nothing this command rewrites'
    : `${withCommas(chars)} credential chars out of ${plan.fields.length} field${plan.fields.length === 1 ? '' : 's'}`
  return `${lead} — ${[...each, ...extra].join(' · ')}`
}

/**
 * Plan the scrub of ONE stored bug row. Pure: it reads the row and returns the write.
 *
 * Run it against the row as it is AT WRITE TIME (inside the guarded writer's `plan`), so the patch
 * is computed from current bytes rather than from a read that may be a minute old.
 */
export function planPastedCredentialScrub(
  row: unknown,
  writable: readonly string[] = PASTED_CREDENTIAL_SCRUB_FIELDS,
): PastedCredentialScrubPlan {
  const empty: PastedCredentialScrubPlan = {
    clean: true, fields: [], patch: {}, findings: [], unwritable: [], report: '', refuse: null,
  }
  if (!row || typeof row !== 'object' || Array.isArray(row)) return { ...empty, report: 'nothing credential-shaped is stored on this bug' }

  const body = row as Record<string, unknown>
  const scrubbable = new Set<string>(writable)
  const fields: PastedCredentialScrubField[] = []
  const unwritable: { field: string; findings: PastedCredentialFinding[] }[] = []
  const findings: PastedCredentialFinding[] = []
  let refuse: string | null = null

  for (const field of writable) {
    const value = body[field]
    if (typeof value !== 'string' || !value) continue
    const found = findPastedCredentials(value)
    if (found.length === 0) continue
    const after = redactPastedCredentials(value)
    if (!scrubKeptEverythingElse(value, after, found)) {
      // Never write a field whose non-credential text moved. The whole point is that this command
      // can only ever subtract a credential.
      refuse = `the redaction of ${field} changed text outside the credential span — refusing to write`
      continue
    }
    fields.push({ field, findings: found, after, chars: found.reduce((n, f) => n + f.chars, 0) })
    findings.push(...found)
  }

  // DETECTION IS TOTAL: look at every other string on the row too, and name what cannot be fixed
  // here. A credential in a field this command does not rewrite must never read as a clean bug.
  for (const [field, value] of Object.entries(body)) {
    if (scrubbable.has(field) || typeof value !== 'string' || !value) continue
    const found = findPastedCredentials(value)
    if (found.length === 0) continue
    unwritable.push({ field, findings: found })
    findings.push(...found)
  }

  if (fields.length === 0 && unwritable.length === 0) {
    return { ...empty, report: 'nothing credential-shaped is stored on this bug' }
  }
  const plan: PastedCredentialScrubPlan = {
    clean: false,
    fields,
    patch: Object.fromEntries(fields.map(f => [f.field, f.after])),
    findings,
    unwritable,
    report: '',
    refuse,
  }
  return { ...plan, report: describePastedCredentialScrub(plan) }
}
